import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT_LAYOUT, UI_CENTER_X, UI_CENTER_Y } from '../core/Config';
import { pulseVirtualAction } from '../core/VirtualControls';
import { VillageDef } from '../types/data';

export class TeleportMenu {
  private readonly container: Phaser.GameObjects.Container;
  private readonly itemLabels: Phaser.GameObjects.Text[] = [];
  private readonly itemBgs: Phaser.GameObjects.Rectangle[] = [];
  private readonly visibleCount: number;
  private readonly rowStartY: number;
  private readonly rowGap: number;
  private villages: VillageDef[] = [];
  private selectedIndex = 0;
  private windowStart = 0;
  private visibleState = false;

  constructor(private readonly scene: Phaser.Scene) {
    const mobile = IS_PORTRAIT_LAYOUT;
    const panelWidth = mobile ? GAME_WIDTH - 56 : 600;
    const panelHeight = mobile ? 1120 : 650;
    this.visibleCount = mobile ? 7 : 10;
    this.rowStartY = mobile ? -350 : -220;
    this.rowGap = mobile ? 102 : 46;

    const dim = scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x050806, 0.62)
      .setOrigin(0.5)
      .setInteractive();
    const shadow = scene.add.rectangle(8, 10, panelWidth, panelHeight, 0x000000, 0.34);
    const outer = scene.add
      .rectangle(0, 0, panelWidth, panelHeight, 0x172019, 0.99)
      .setStrokeStyle(mobile ? 5 : 3, 0xe8c768, 0.96);
    const inner = scene.add
      .rectangle(0, 0, panelWidth - 28, panelHeight - 28, 0x0c120f, 0.99)
      .setStrokeStyle(2, 0x8ebc98, 0.72);
    const title = scene.add.text(0, mobile ? -486 : -274, '마을 이동', {
      fontSize: mobile ? '42px' : '30px',
      color: '#fff0b5',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    const help = scene.add.text(0, mobile ? -430 : -236, mobile ? '이동할 마을을 누르세요' : '방향키 선택 · Enter 이동 · Esc 닫기', {
      fontSize: mobile ? '27px' : '17px',
      color: '#b9ddbf',
    }).setOrigin(0.5);
    const closeBg = scene.add
      .rectangle(panelWidth / 2 - (mobile ? 74 : 58), -panelHeight / 2 + (mobile ? 62 : 42), mobile ? 116 : 88, mobile ? 66 : 42, 0x3a4438, 0.98)
      .setStrokeStyle(2, 0xa9c3a4, 0.75)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => pulseVirtualAction('cancel'));
    const closeText = scene.add.text(closeBg.x, closeBg.y, '닫기', {
      fontSize: mobile ? '28px' : '18px',
      color: '#f2f4ed',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    const children: Phaser.GameObjects.GameObject[] = [dim, shadow, outer, inner, title, help, closeBg, closeText];
    for (let slot = 0; slot < this.visibleCount; slot += 1) {
      const y = this.rowStartY + slot * this.rowGap;
      const bg = scene.add
        .rectangle(0, y, panelWidth - (mobile ? 78 : 70), mobile ? 84 : 38, 0x233129, 0.9)
        .setStrokeStyle(2, 0x6f8d75, 0.55)
        .setInteractive({ useHandCursor: true })
        .on('pointerdown', () => {
          const index = this.windowStart + slot;
          if (!this.villages[index]) return;
          this.selectedIndex = index;
          this.render();
          pulseVirtualAction('confirm');
        });
      const label = scene.add.text(-panelWidth / 2 + (mobile ? 72 : 56), y - (mobile ? 25 : 13), '', {
        fontSize: mobile ? '31px' : '20px',
        color: '#eef4ec',
      });
      children.push(bg, label);
      this.itemBgs.push(bg);
      this.itemLabels.push(label);
    }

    const pageY = mobile ? 410 : 268;
    const previousBg = scene.add
      .rectangle(-panelWidth * 0.22, pageY, mobile ? 260 : 190, mobile ? 72 : 44, 0x2d3d31, 0.98)
      .setStrokeStyle(2, 0x8ebc98, 0.68)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.page(-1));
    const nextBg = scene.add
      .rectangle(panelWidth * 0.22, pageY, mobile ? 260 : 190, mobile ? 72 : 44, 0x2d3d31, 0.98)
      .setStrokeStyle(2, 0x8ebc98, 0.68)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.page(1));
    const previousText = scene.add.text(previousBg.x, previousBg.y, '이전', {
      fontSize: mobile ? '28px' : '18px',
      color: '#f2f4ed',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    const nextText = scene.add.text(nextBg.x, nextBg.y, '다음', {
      fontSize: mobile ? '28px' : '18px',
      color: '#f2f4ed',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    children.push(previousBg, nextBg, previousText, nextText);

    this.container = scene.add
      .container(UI_CENTER_X, UI_CENTER_Y, children)
      .setScrollFactor(0)
      .setDepth(12000)
      .setVisible(false);
  }

  setVillages(villages: VillageDef[], currentVillageId?: string) {
    this.villages = villages;
    const currentIndex = villages.findIndex((item) => item.id === currentVillageId);
    this.selectedIndex = currentIndex >= 0 ? currentIndex : 0;
    this.render();
  }

  toggle(currentVillageId?: string) {
    if (currentVillageId) {
      const currentIndex = this.villages.findIndex((item) => item.id === currentVillageId);
      if (currentIndex >= 0) this.selectedIndex = currentIndex;
    }
    this.visibleState = !this.visibleState;
    this.container.setVisible(this.visibleState);
    this.render();
  }

  hide() {
    this.visibleState = false;
    this.container.setVisible(false);
  }

  isVisible() {
    return this.visibleState;
  }

  move(delta: number) {
    if (!this.visibleState || this.villages.length === 0) return;
    this.selectedIndex = Phaser.Math.Wrap(this.selectedIndex + delta, 0, this.villages.length);
    this.render();
  }

  getSelectedVillageId(): string | undefined {
    return this.villages[this.selectedIndex]?.id;
  }

  private page(direction: number) {
    if (!this.villages.length) return;
    this.selectedIndex = Phaser.Math.Clamp(
      this.selectedIndex + direction * this.visibleCount,
      0,
      this.villages.length - 1,
    );
    this.render();
  }

  private render() {
    const maxStart = Math.max(0, this.villages.length - this.visibleCount);
    this.windowStart = Phaser.Math.Clamp(
      this.selectedIndex - Math.floor(this.visibleCount / 2),
      0,
      maxStart,
    );

    this.itemLabels.forEach((label, slot) => {
      const index = this.windowStart + slot;
      const village = this.villages[index];
      const selected = index === this.selectedIndex;
      label
        .setText(village ? `${selected ? '> ' : '  '}${village.name}  ·  ${village.id}` : '')
        .setColor(selected ? '#ffe391' : '#eef4ec')
        .setStyle({ fontStyle: selected ? 'bold' : 'normal' })
        .setVisible(Boolean(village));
      this.itemBgs[slot]
        .setVisible(Boolean(village))
        .setFillStyle(selected ? 0x4d684f : 0x233129, selected ? 0.96 : 0.86)
        .setStrokeStyle(2, selected ? 0xe8c768 : 0x6f8d75, selected ? 0.92 : 0.55);
    });
  }
}
